import React, {
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  Outlet,
  useLocation,
  useNavigate
} from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { ApplicationRoutes, StaticRoutes } from '../../Router';
import {
  ApplyHeader
} from '../../components';
import {
  ApplicationSteps,
  submissionStates
} from '../../constants';
import {
  refreshTriggerState,
  applicationsList
} from '../../recoil';

const ARRAY_LAST_IDX = -1;

const styles = {
  outletContainer: {
    width: '100%'
  }
};

const stepKeysSorted = Object.keys(ApplicationSteps)?.filter(
  (s) => ApplicationSteps[s].paths?.length > 0
).sort(
  (a, b) => ApplicationSteps[a].stepNum - ApplicationSteps[b].stepNum
);

const findStepKey = (url) => {
  return Object
    .keys(ApplicationSteps)
    .find((k) => ApplicationSteps[k].paths?.includes(url));
};

const findSubStepNum = (stepDetails, url) => stepDetails?.paths?.findIndex?.((p) => p === url) || ARRAY_LAST_IDX;

const findNextStep = (currentStepKey) => stepKeysSorted.find(
  (k) => ApplicationSteps[currentStepKey]?.stepNum < ApplicationSteps[k].stepNum
) || stepKeysSorted[0];

const wizardSteps = Object.values(ApplicationSteps).map((s) => ({
  label: s.title,
  substeps: s.paths?.length || 1
}));

export const LoanApplication = () => {

  const [applicationSteps, setApplicationSteps] = useState(ApplicationSteps);
  const setRefreshTrigger = useSetRecoilState(refreshTriggerState);

  const navigate = useNavigate();
  const location = useLocation();

  const [currentStepKey, setCurrentStepKey] = useState(findStepKey(location.pathname));
  const appsList = useRecoilValue(applicationsList);

  const navigateTo = (path) =>
    navigate(path, {
      state: {
        returnPath: location.pathname
      }
    });

  const redirectBack = () => {
    if (!Array.isArray(appsList) || appsList.length === 0) {
      navigate(StaticRoutes.Products);
    }else{
      navigateTo('/dashboard');
    }
    return;
  };

  const findPrevStep = (currentStepKey) => stepKeysSorted.findLast(
    (k) => ApplicationSteps[currentStepKey]?.stepNum > ApplicationSteps[k].stepNum
  ) || redirectBack();

  const currentStep = useMemo(() => {
    let appSteps = ApplicationSteps;
    setApplicationSteps(appSteps);

    let localStorageStepKey = localStorage.getItem('loanApplicationStepKey');

    if (!localStorageStepKey) {
      localStorageStepKey = 'personalDetails';

      localStorage.setItem('loanApplicationStepKey', localStorageStepKey);
    }

    if (appSteps[currentStepKey]?.stepNum > appSteps[localStorageStepKey]?.stepNum) {
      setCurrentStepKey(localStorageStepKey);
      return appSteps[localStorageStepKey];
    }
    return appSteps[currentStepKey];
  }, [currentStepKey]);

  const stepNum = useMemo(() => {
    if (applicationSteps) {
      return applicationSteps[findStepKey(location.pathname)]?.stepNum - 1 || 0;
    }
    return 0;
  }, [location.pathname, applicationSteps]);

  const numSubSteps = useMemo(() => {
    if (applicationSteps) {
      return applicationSteps[findStepKey(location.pathname)]?.paths?.length || 0;
    }
    return 0;
  }, [location.pathname, applicationSteps]);

  const [subStepNum, setSubStepNum] = useState(findSubStepNum(currentStep, location.pathname));

  const navPath = useMemo(() => currentStep?.paths?.[subStepNum] || '', [currentStep, subStepNum]);

  const [submitState, setSubmitState] = useState(submissionStates.default);

  const goToNextPage = () => {
    const sub = subStepNum + 1;
    const nextStep = findNextStep(currentStepKey);
    setRefreshTrigger((prev) => prev + 1);
    if (sub < numSubSteps) {
      setSubStepNum(sub);
    } else {
      setSubStepNum(0);
      setCurrentStepKey(nextStep);
      localStorage.setItem('loanApplicationStepKey', nextStep);
    }
  };

  const goToPreviousPage = () => {
    const sub = subStepNum - 1;
    const prevStep = findPrevStep(currentStepKey);
    if (sub < 0 || !('businessId' in localStorage)) {
      setCurrentStepKey(prevStep);
      setSubStepNum((applicationSteps[prevStep]?.paths?.length || 1) - 1);
      localStorage.setItem('loanApplicationStepKey', prevStep || 'personalDetails');
    } else {
      setSubStepNum(sub);
    }
  };

  const backClick = () => {
    goToPreviousPage();
  };

  const nextClick = () => {
    if (submitState === submissionStates.canSubmit) {
      setSubmitState(submissionStates.startSubmit);
      const loanApplicationStepKey = localStorage.getItem('loanApplicationStepKey');
      if (loanApplicationStepKey === 'fundingDetails') {
        localStorage.removeItem('loanApplicationStepKey');
      }
    }
  };

  useEffect(() => {

    if (navPath) {
      navigate(navPath);
    } else {
      goToNextPage();
    }
  }, [navPath]);

  useEffect(() => {
    if (submitState === submissionStates.submitSuccess) {
      goToNextPage();
    }
  }, [submitState]);

  useEffect(() => {
    if (location.pathname === '/apply' || location.pathname === '/apply/') {
      navigate(ApplicationRoutes.personalDetails);
      return;
    }
  }, [location.pathname]);

  const subStepCurrentNum = useMemo(() => {
    if (applicationSteps) {
      const resp = findSubStepNum(applicationSteps[findStepKey(location.pathname)], location.pathname);
      if (resp === ARRAY_LAST_IDX) {
        return 0;
      }
      return resp;
    }
    return 0;
  }, [location.pathname, applicationSteps]);

  useEffect(() => {
    const loanApplicationStepKey = localStorage.getItem('loanApplicationStepKey');
    if (loanApplicationStepKey !== findStepKey(location.pathname)) {
      navigate(ApplicationRoutes[loanApplicationStepKey]);
    }
  }, [location, navigate]);

  return (
    <>
      {currentStep && <ApplyHeader
        currentStep={stepNum}
        currentSubStep={stepNum}
        activeSubStep={subStepCurrentNum}
        steps={wizardSteps}
        hideProgress={false}
        hideBackButton={false}
        onBackClick={backClick}
      />}

      <div style={styles.outletContainer}>
        <Outlet context={{ submitState, setSubmitState, backClick, submissionStates, nextClick }} />

        {/* {numSubSteps > 1 && <FlatLinePager numSteps={numSubSteps} activeStep={subStepCurrentNum} />} */}
      </div>
    </>
  );
};
